import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types"; // Import PropTypes for prop validation
import "./DropdownWrapper.scss";

// Custom hook for managing the dropdown's state and position
const useDropdown = (direction = "right") => {
  const [isOpen, setIsOpen] = useState(false); // Tracks whether the dropdown is open or closed
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Stores dropdown position
  const dropdownRef = useRef(null); // Ref for the dropdown content
  const toggleElementRef = useRef(null); // Ref for the element that triggers the dropdown

  // Function to toggle the dropdown's open/closed state
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Function to close the dropdown
  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Handles clicks outside of the dropdown to close it
  const handleOutsideClick = (e) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      toggleElementRef.current &&
      !toggleElementRef.current.contains(e.target)
    ) {
      closeDropdown();
    }
  };

  // Updates the dropdown position to avoid overflow on the screen
  const updateDropdownPosition = () => {
    if (toggleElementRef.current && dropdownRef.current) {
      const toggleRect = toggleElementRef.current.getBoundingClientRect(); // Get toggle element's position
      const dropdownRect = dropdownRef.current.getBoundingClientRect(); // Get dropdown's dimensions
      const viewportWidth = window.innerWidth; // Width of the viewport
      const viewportHeight = window.innerHeight; // Height of the viewport

      let top = toggleRect.bottom; // Default: dropdown appears below the toggle
      let left = toggleRect.left; // Default: dropdown starts at toggle's left edge

      if (direction === "right") {
        // Start from the left edge, grow to the right
        left = toggleRect.left;

        // If there's no space to the right, switch to the left
        if (toggleRect.left + dropdownRect.width > viewportWidth) {
          left = toggleRect.right - dropdownRect.width; // Align to the right edge and grow left
        }
      } else if (direction === "left") {
        // Start from the right edge, grow to the left
        left = toggleRect.right - dropdownRect.width;

        // If there's no space to the left, switch to the right
        if (toggleRect.right - dropdownRect.width < 0) {
          left = toggleRect.left; // Align to the left edge and grow right
        }
      }

      // Handle vertical overflow: if there's no space below, place it above
      if (toggleRect.bottom + dropdownRect.height > viewportHeight) {
        top = toggleRect.top - dropdownRect.height; // Position above
      }

      // Prevent the dropdown from going out of view on the top
      if (top < 0) {
        top = toggleRect.bottom; // Reset to below if no space above
      }

      // Update the dropdown position state
      setDropdownPosition({ top, left });
    }
  };

  // Event listener to close dropdown if clicked outside
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Update dropdown position when it is opened or when scrolling occurs
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      // Add scroll event listener to update position dynamically
      const handleScroll = () => {
        updateDropdownPosition();
      };

      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen]);

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  // Close dropdown if the parent element goes out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          closeDropdown(); // Close the dropdown if the parent is not visible
        }
      },
      { threshold: 0.1 } // Trigger when at least 10% of the element is visible
    );

    if (toggleElementRef.current) {
      observer.observe(toggleElementRef.current);
    }

    return () => {
      if (toggleElementRef.current) {
        observer.unobserve(toggleElementRef.current);
      }
    };
  }, []);

  // Return values and functions to control dropdown behavior
  return {
    isOpen,
    toggleDropdown,
    closeDropdown,
    dropdownRef,
    toggleElementRef,
    dropdownPosition,
  };
};

// DropdownWrapper component to render the dropdown toggle button and dropdown content
const DropdownWrapper = ({
  children,
  toggleElement,
  className,
  direction = "right",
}) => {
  const {
    isOpen,
    toggleDropdown,
    dropdownRef,
    toggleElementRef,
    dropdownPosition,
    closeDropdown,
  } = useDropdown(direction); // Pass direction prop to the hook

  // Handle click on children to close the dropdown
  // const handleChildClick = () => {
  //   closeDropdown(); // Close the dropdown when any child is clicked
  // };

  return (
    <div className="dropdown-wrapper">
      <button
        ref={toggleElementRef}
        onClick={toggleDropdown}
        className="dropdownwrapper-toggle"
      >
        {toggleElement}
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`dropdownwrapper-content ${className}`}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <div>{children}</div>
        </div>
      )}
    </div>
  );
};

// PropTypes validation for DropdownWrapper
DropdownWrapper.propTypes = {
  children: PropTypes.node.isRequired, // Dropdown content (can be any renderable React node)
  toggleElement: PropTypes.node.isRequired, // Element that triggers the dropdown
  direction: PropTypes.oneOf(["left", "right"]), // Direction the dropdown should open
};

DropdownWrapper.defaultProps = {
  direction: "right", // Default to opening to the right
};

// Export the hook and component
export { useDropdown, DropdownWrapper };
